import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../../shared/hooks/useApi/useAuth';
import { useStandardAlertStore } from '../../../shared/store/portal/alertStore';
import { getAlertMessage } from '../../../shared/constants/alertMessages';
import { getModalMessage } from '../../../shared/constants/modalMessages';
import { getBtnName } from '../../../shared/constants/btnNames';
import MoleculeAuthInput from '../molecules/MoleculeAuthInput';
import { BlackFullWidthBtn } from '../../../shared/component/headless/button/BtnBody';
import { AlertPosition, AlertType, DividerType, ModalType } from '../../../shared/enum/EnumRepository';
import { useModalStore } from '../../../shared/store/portal/modalStore';
import { useLoadingStore } from '../../../shared/store/loadingStore';
import { useValidation } from '../../../shared/hooks/useValidation';
import SearchForgot from '../SearchForgot';
import Divider from '../../../shared/Divider';
import UniversalIconBtn from '../../../shared/component/headless/button/UniversalIconBtn';
import belowIcon from '../../../asset/icon/below.png';

interface ModalProps {
  setIsUserInput: (isUserInput: boolean) => void;
}

const LoginMobile: React.FC<ModalProps> = ({ setIsUserInput }) => {
  const { login } = useAuth();
  const { isLoading } = useLoadingStore();
  const { clearModal, setStandardModal } = useModalStore();
  const { enqueueAlert } = useStandardAlertStore();
  const { isEmail } = useValidation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (email.length > 0 || password.length > 0) {
      setIsUserInput(true);
    } else {
      setIsUserInput(false);
    }
  }, [email, password]);

  useEffect(() => {
    if (modalRef.current) {
      modalRef.current.focus();
    }
  }, []);

  const validateEmail = (value: string) => {
    if (value.length === 0) {
      setEmailError('');
      return true;
    }
    if (!isEmail(value)) {
      setEmailError('Invalid email format');
      return false;
    }
    setEmailError('');
    return true;
  };

  const changeEmailInput = (value: string) => {
    validateEmail(value);
    setEmail(value);
  }

  const validatePassword = (value: string) => {
    if (value.length === 0) {
      setPasswordError('');
      return true;
    }
    setPasswordError('');
    return true;
  };

  const changePasswordInput = (value: string) => {
    validatePassword(value);
    setPassword(value);
  }

  const isEmpty = () => {
    return (email === '' || password === '')
  }

  const isValid = () => {
    return (!emailError && !passwordError)
  }

  const handleSubmit = async () => {
    if (isEmpty()) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('MISSING_REQUIRED_FIELD')
      })
      return;
    }
    if (!isValid()) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('CHECK_FORMAT')
      })
      return;
    }
    await login({ email, password });
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (isLoading) {
      return null;
    }
    switch (event.key) {
      case 'Enter':
        handleSubmit();
        break;
      case 'Escape':
        clearModal();
        break;
      default:
        break;
    }
  };

  const openRegisterModal = () => {
    setStandardModal({
      modalType: ModalType.REGISTER,
      title: null,
      value: null,
      handleChange: () => {},
    });
  };

  return (
    <LoginComp 
      ref={modalRef} 
      onKeyDown={handleKeyDown} 
      tabIndex={-1}
    >
      <MainContainer>
        <TitleWrapper>
          <Title>{getModalMessage('LOGIN_TITLE')}</Title>
          <UniversalIconBtn
            size="fit"
            border={false}
            isTransparent
            backgroundImg={belowIcon}
            onClick={() => clearModal(400)}
          />
        </TitleWrapper>
        <MoleculeAuthInput
          name={getModalMessage('AUTH_EMAIL_LABEL')}
          value={email}
          handleChange={changeEmailInput}
          err={emailError}
          placeholder={getModalMessage('AUTH_EMAIL_LABEL')}
          nameVisible={false}
          errVisible={false}
        />
        <MoleculeAuthInput
          name={getModalMessage('AUTH_PASSWORD_LABEL')}
          value={password}
          handleChange={changePasswordInput}
          err={passwordError}
          placeholder={getModalMessage('AUTH_PASSWORD_LABEL')}
          nameVisible={false}
          errVisible={false}
        />
        <SearchForgot />
        <SubmitButton onClick={handleSubmit} disabled={isLoading > 0}>{getBtnName('LOGIN')}</SubmitButton>
      <Divider dividerType={DividerType.PLAIN} bottom="30px" />
      </MainContainer>
      <SubContainer>
        <ToggleText onClick={openRegisterModal}>
          <span>{getModalMessage('LOGIN_NO_ACCOUNT')}</span>
          <RegisterText>{getBtnName('REGISTER')}</RegisterText>
        </ToggleText>
      </SubContainer>
    </LoginComp>
  );
};

const LoginComp = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 4px;
`

const MainContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: clamp(18px, 2.5vh, 40px);
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: clamp(20px, 8vh, 40px);
`;

const Title = styled.h2`
  color: ${({ theme }) => theme.colors.color_Gray_02};
  ${({ theme }) => theme.typography.Body_00_1};
`;

const SubmitButton = styled(BlackFullWidthBtn)`
  margin: 25px 0px 15px 0px;
`;

const SubContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
`;

const ToggleText = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-start;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_03_2};
`;

const RegisterText = styled.span`
  font-style: italic;
  text-decoration: underline;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  &:hover {
    transform: scale(1.05);
    color: ${({ theme }) => theme.colors.color_Gray_03};
  }
`;


export default LoginMobile;
